import React, { useState, useContext, useCallback } from 'react'
import { M_Table, LoadingContext, NavigateContext, M_MessageBoard, M_DrawerTableTextField, M_Button, BufferService, M_Select, M_InputTextField, M_TextArea, M_ElementDrawer } from 'src/components/M_Components'
import { useTranslation } from 'react-i18next'
import { DeleteOutline, EuroSymbol, InfoOutlined } from '@mui/icons-material'
import './boards.css'

const getCardValue = (value, fallback) => value || fallback

const TrinomialDetail = ({ label, value, icon }) => (
    <div className='trinomialDrawerDetail'>
        <div className='trinomialDrawerDetailLabel'>
            <img src={icon} alt={label} className='trinomialDrawerDetailIcon' />
            <span>{label}</span>
        </div>
        <div className='trinomialDrawerDetailValue'>{value}</div>
    </div>
)

const TrinomialCard = ({ trinomial, selected, onSelect, t }) => (
    <div className={`trinomialDrawerCard ${selected ? 'trinomialDrawerCardSelected' : ''}`}>
        <div className='trinomialDrawerCardHeader'>
            <div>
                <div className='trinomialDrawerCardEyebrow'>{t('Trinomial')}</div>
            </div>
            {selected && <div className='trinomialDrawerCardBadge'><i className='pi pi-check' /></div>}
        </div>

        <div className='trinomialDrawerCardGrid'>
            <div className='trinomialDrawerButtonSlot'>
                <M_Button
                    backgroundColor={selected ? 'var(--color-green)' : 'var(--color-blue)'}
                    label={t('Select')}
                    labelColor={'var(--color-white)'}
                    onClick={onSelect}
                />
            </div>
            <TrinomialDetail
                label={t('Transport')}
                value={getCardValue(trinomial.TransportLicensePlate, t('NoData'))}
                icon='/icons/svgsDaNet/truck.svg'
            />
            <TrinomialDetail
                label={t('Trailer')}
                value={getCardValue(trinomial.TrailerLicensePlate, t('NoData'))}
                icon='/icons/svgsDaNet/trailer.svg'
            />
            <TrinomialDetail
                label={t('Driver')}
                value={getCardValue(trinomial.DriverName, t('NoData'))}
                icon='/icons/svgsDaNet/user.svg'
            />
        </div>
    </div>
)

const orderBoardRowHighlightRules = [
    {
        className: 'orderBoardRowRoro',
        condition: (rowData) =>
            !!rowData?.ContainerPreLevante ||
            !!rowData?.ContainerPreLevanteCustomer
    }
]

const getOrderBoardRowClassName = (rowData) =>
    orderBoardRowHighlightRules
        .filter((rule) => rule.condition(rowData))
        .map((rule) => rule.className)
        .join(' ')

const Body = ({ dataChangeState, dataMovements, setDataMovements, t, openTrinomialDrawer, optionsTrinomial = [], serviceOrderId, setInfoTrinomial }) => {
    const selectedSubcontractedCosts = dataMovements.SubcontractedCosts || []

    const setSubcontractedCosts = (costs) => {
        const uniqueCosts = (costs || []).reduce((acc, cost) => {
            if (!cost?.CostsId || acc.some(item => item.CostsId === cost.CostsId)) return acc

            return [...acc, {
                CostsId: cost.CostsId,
                Descriptive: cost.Descriptive || cost.Name || '',
                AddicionalPrice: cost.AddicionalPrice ?? cost.Value ?? null,
            }]
        }, [])

        setDataMovements(prev => ({
            ...prev,
            SubcontractedCosts: uniqueCosts
        }))
    }

    const removeSubcontractedCost = (costsId) => {
        setDataMovements(prev => ({
            ...prev,
            SubcontractedCosts: (prev.SubcontractedCosts || []).filter(cost => cost.CostsId !== costsId)
        }))
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
            {/* movimento falta de informação do cliente ✅ */}
            {dataChangeState === 'FI' && (
                <M_TextArea
                    label={t('Notes')}
                    value={dataMovements.Notes || ''}
                    onChange={value => {
                        setDataMovements(prev => ({
                            ...prev,
                            Notes: value.target.value
                        }))
                    }}
                />
            )}
            {/* movimento atribuição trinomio ✅ */}
            {dataChangeState === 'AT' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '16px'
                    }}
                    >
                        {optionsTrinomial.map((elem, key) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ flex: 1 }}>
                                    <M_DrawerTableTextField
                                        width="100%"
                                        tableLabel={elem.NormalizedName}
                                        label={elem.NormalizedName}
                                        required={elem.IsRequired}
                                        entity={elem.ForeignTableName}
                                        value={
                                            dataMovements[elem.ForeignTableToPassId]
                                                ? {
                                                    [elem.ForeignTableToPassId]: dataMovements[elem.ForeignTableToPassId],
                                                    [elem.ForeignKeyName]: dataMovements[elem.Name],
                                                }
                                                : null
                                        }
                                        filterField={elem.ForeignKeyName}
                                        labelFields={[elem.ForeignKeyName]}
                                        onSubmitValue={obj => {
                                            if (obj) {
                                                setDataMovements(prev => ({
                                                    ...prev,
                                                    [elem.Name]: obj[elem.ForeignKeyName],
                                                    [elem.ForeignTableToPassId]: obj[elem.ForeignTableToPassId],
                                                    Transport: obj.TransportLicensePlate || null,
                                                    TransportId: obj.TransportId || null,
                                                    Trailer: obj.TrailerLicensePlate || null,
                                                    TrailerId: obj.TrailerId || null,
                                                    Driver: obj.DriverName || null,
                                                    DriverId: obj.DriverId || null,
                                                }))
                                            } else {
                                                setDataMovements(prev => ({
                                                    ...prev,
                                                    [elem.Name]: null,
                                                    [elem.ForeignTableToPassId]: null,
                                                    Transport: null,
                                                    TransportId: null,
                                                    Trailer: null,
                                                    TrailerId: null,
                                                    Driver: null,
                                                    DriverId: null,
                                                }))
                                            }
                                        }}
                                        valueField={elem.ForeignTableToPassId}
                                        query={serviceOrderId ? `serviceOrderId=${serviceOrderId}` : elem.filter}
                                        // filtersOnTable
                                        inputFilter
                                    />
                                </div>

                                <M_Button
                                    labelColor={'var(--color-white)'}
                                    onClick={() => setInfoTrinomial(true)}
                                    icon={<InfoOutlined color='info' />}
                                />
                            </div>
                        ))}
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '16px'
                    }}
                    >
                        <M_DrawerTableTextField
                            width="100%"
                            tableLabel={t('Tractor')}
                            label={t('Tractor')}
                            entity="ServiceOrderTransportCompatibilityView"
                            value={
                                dataMovements?.TransportId
                                    ? { TransportId: dataMovements.TransportId, Transport: dataMovements.Transport }
                                    : null
                            }
                            filterField="Transport"
                            labelFields={['Transport']}
                            onSubmitValue={obj => {
                                setDataMovements(prev => ({
                                    ...prev,
                                    Transport: obj ? obj.LicensePlate : null,
                                    TransportId: obj ? obj.TransportId : null,
                                }))
                            }}
                            valueField="TransportId"
                            // disabled={!dataMovements?.TransportId}
                            query={serviceOrderId ? `serviceOrderId=${serviceOrderId}` : null}
                        />
                        <M_DrawerTableTextField
                            width="100%"
                            tableLabel={t('Trailer')}
                            label={t('Trailer')}
                            entity="ServiceOrderTrailerCompatibilityView"
                            value={
                                dataMovements?.TrailerId
                                    ? { TrailerId: dataMovements.TrailerId, Trailer: dataMovements.Trailer }
                                    : null
                            }
                            filterField="Trailer"
                            labelFields={['Trailer']}
                            onSubmitValue={obj => {
                                setDataMovements(prev => ({
                                    ...prev,
                                    Trailer: obj ? obj.LicensePlate : null,
                                    TrailerId: obj ? obj.TrailerId : null,
                                }))
                            }}
                            valueField="TrailerId"
                            // disabled={!dataMovements?.TransportId}
                            query={serviceOrderId ? `serviceOrderId=${serviceOrderId}` : null}
                        />

                        <M_DrawerTableTextField
                            width="100%"
                            tableLabel={t('Driver')}
                            label={t('Driver')}
                            entity="ServiceOrderDriverCompatibilityView"
                            value={
                                dataMovements?.DriverId
                                    ? { DriverId: dataMovements.DriverId, Driver: dataMovements.Driver }
                                    : null
                            }
                            filterField="Driver"
                            labelFields={['Driver']}
                            onSubmitValue={obj => {
                                setDataMovements(prev => ({
                                    ...prev,
                                    Driver: obj ? obj.Name : null,
                                    DriverId: obj ? obj.DriverId : null,
                                }))
                            }}
                            valueField="DriverId"
                            // disabled={!dataMovements?.TransportId}
                            query={serviceOrderId ? `serviceOrderId=${serviceOrderId}` : null}
                        />

                        <M_InputTextField
                            label={t('DateTimeStart')}
                            type="datetime-local"
                            value={dataMovements.DateTime || ''}
                            onChange={value => {
                                setDataMovements(prev => ({
                                    ...prev,
                                    DateTime: value.target.value
                                }))
                            }}
                        />
                    </div>

                </div>
            )}

            {/* movimento atribuir numero de contentor (Levantar contentor e Entrega do contentor) ✅ */}
            {(dataChangeState === 'LC' || dataChangeState === 'EC') && (
                <M_InputTextField
                    label={t("ContainerNumber")}
                    value={dataMovements.ContainerNumber || ''}
                    onChange={value => {
                        setDataMovements(prev => ({
                            ...prev,
                            ContainerNumber: value.target.value
                        }))
                    }}
                />
            )}

            {/* movimento incidencia */}
            {dataChangeState === 'IN' && (
                <div>
                    <M_DrawerTableTextField
                        width="100%"
                        tableLabel={t('Incidence')}
                        label={t('Incidence')}
                        entity="ServiceOrderStateReason"
                        value={
                            dataMovements?.ReasonId
                                ? { ReasonId: dataMovements.ReasonId, Name: dataMovements.Name }
                                : null
                        }
                        filterField="Name"
                        labelFields={['Name']}
                        onSubmitValue={obj =>
                            setDataMovements(prev => ({
                                ...prev,
                                Name: obj ? obj.Name : null,
                                ReasonId: obj ? obj.ReasonId : null,
                            }))
                        }
                        valueField="ReasonId"
                    />
                    <M_TextArea
                        label={t('Notes')}
                        value={dataMovements.Notes || ''}
                        onChange={value => {
                            setDataMovements(prev => ({
                                ...prev,
                                Notes: value.target.value
                            }))
                        }}
                    />
                </div>
            )}
            {/* movimento atribuir pré-levante */}
            {dataChangeState === 'AP' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '16px'
                    }}
                    >
                        <M_DrawerTableTextField
                            width="100%"
                            tableLabel={t('Address')}
                            label={t('AddressPreLevante')}
                            entity="Address"
                            value={
                                dataMovements?.PreLevanteAddressId
                                    ? {
                                        AddressId: dataMovements.PreLevanteAddressId,
                                        AddressCode: dataMovements.PreLevanteAddress
                                    }
                                    : null
                            }
                            filterField="AddressCode"
                            labelFields={['AddressCode']}
                            onSubmitValue={obj =>
                                setDataMovements(prev => ({
                                    ...prev,
                                    PreLevanteAddress: obj ? obj.AddressCode : null,
                                    PreLevanteAddressId: obj ? obj.AddressId : null,
                                }))
                            }
                            valueField="AddressId"
                        />

                        <M_InputTextField
                            label={t("PreLevanteDate")}
                            type="datetime-local"
                            value={dataMovements.PreLevanteDate || ''}
                            onChange={value => {
                                setDataMovements(prev => ({
                                    ...prev,
                                    PreLevanteDate: value.target.value
                                }))
                            }}
                        />

                        <M_InputTextField
                            label={t('DateTimeStart')}
                            type="datetime-local"
                            value={dataMovements.DateTime || ''}
                            onChange={value => {
                                setDataMovements(prev => ({
                                    ...prev,
                                    DateTime: value.target.value
                                }))
                            }}
                        />
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '16px'
                    }}
                    >
                        {optionsTrinomial.map((elem, key) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ flex: 1 }}>
                                    <M_DrawerTableTextField
                                        width="100%"
                                        tableLabel={elem.NormalizedName}
                                        label={elem.NormalizedName}
                                        required={elem.IsRequired}
                                        entity={elem.ForeignTableName}
                                        value={
                                            dataMovements[elem.ForeignTableToPassId]
                                                ? {
                                                    [elem.ForeignTableToPassId]: dataMovements[elem.ForeignTableToPassId],
                                                    [elem.ForeignKeyName]: dataMovements[elem.Name],
                                                }
                                                : null
                                        }
                                        filterField={elem.ForeignKeyName}
                                        labelFields={[elem.ForeignKeyName]}
                                        onSubmitValue={obj => {
                                            if (obj) {
                                                setDataMovements(prev => ({
                                                    ...prev,
                                                    [elem.Name]: obj[elem.ForeignKeyName],
                                                    [elem.ForeignTableToPassId]: obj[elem.ForeignTableToPassId],
                                                    Transport: obj.TransportLicensePlate || null,
                                                    TransportId: obj.TransportId || null,
                                                    Trailer: obj.TrailerLicensePlate || null,
                                                    TrailerId: obj.TrailerId || null,
                                                    Driver: obj.DriverName || null,
                                                    DriverId: obj.DriverId || null,
                                                }))
                                            } else {
                                                setDataMovements(prev => ({
                                                    ...prev,
                                                    [elem.Name]: null,
                                                    [elem.ForeignTableToPassId]: null,
                                                    Transport: null,
                                                    TransportId: null,
                                                    Trailer: null,
                                                    TrailerId: null,
                                                    Driver: null,
                                                    DriverId: null,
                                                }))
                                            }
                                        }}
                                        valueField={elem.ForeignTableToPassId}
                                        query={serviceOrderId ? `serviceOrderId=${serviceOrderId}` : elem.filter}
                                        filtersOnTable
                                    />
                                </div>

                                <M_Button
                                    labelColor={'var(--color-white)'}
                                    onClick={() => setInfoTrinomial(true)}
                                    icon={<InfoOutlined color='info' />}
                                />
                            </div>
                        ))}
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                        gap: '16px'
                    }}
                    >
                        <M_DrawerTableTextField
                            width="100%"
                            tableLabel={t('Tractor')}
                            label={t('Tractor')}
                            entity="ServiceOrderTransportCompatibilityView"
                            value={
                                dataMovements?.TransportId
                                    ? { TransportId: dataMovements.TransportId, Transport: dataMovements.Transport }
                                    : null
                            }
                            filterField="Transport"
                            labelFields={['Transport']}
                            onSubmitValue={obj => {
                                setDataMovements(prev => ({
                                    ...prev,
                                    Transport: obj ? obj.LicensePlate : null,
                                    TransportId: obj ? obj.TransportId : null,
                                }))
                            }}
                            valueField="TransportId"
                            // disabled={!dataMovements?.TransportId}
                            query={serviceOrderId ? `serviceOrderId=${serviceOrderId}` : null}
                        />

                        <M_DrawerTableTextField
                            width="100%"
                            tableLabel={t('Trailer')}
                            label={t('Trailer')}
                            entity="ServiceOrderTrailerCompatibilityView"
                            value={
                                dataMovements?.TrailerId
                                    ? { TrailerId: dataMovements.TrailerId, Trailer: dataMovements.Trailer }
                                    : null
                            }
                            filterField="Trailer"
                            labelFields={['Trailer']}
                            onSubmitValue={obj => {
                                setDataMovements(prev => ({
                                    ...prev,
                                    Trailer: obj ? obj.LicensePlate : null,
                                    TrailerId: obj ? obj.TrailerId : null,
                                }))
                            }}
                            valueField="TrailerId"
                            // disabled={!dataMovements?.TransportId}
                            query={serviceOrderId ? `serviceOrderId=${serviceOrderId}` : null}
                        />

                        <M_DrawerTableTextField
                            width="100%"
                            tableLabel={t('Driver')}
                            label={t('Driver')}
                            entity="ServiceOrderDriverCompatibilityView"
                            value={
                                dataMovements?.DriverId
                                    ? { DriverId: dataMovements.DriverId, Driver: dataMovements.Driver }
                                    : null
                            }
                            filterField="Driver"
                            labelFields={['Driver']}
                            onSubmitValue={obj => {
                                setDataMovements(prev => ({
                                    ...prev,
                                    Driver: obj ? obj.Name : null,
                                    DriverId: obj ? obj.DriverId : null,
                                }))
                            }}
                            valueField="DriverId"
                            // disabled={!dataMovements?.TransportId}
                            query={serviceOrderId ? `serviceOrderId=${serviceOrderId}` : null}
                        />
                    </div>
                </div>
            )}
            {/* movimento subContratar */}
            {dataChangeState === 'SC' && (
                <div>
                    {/* alterar para um input de consulta de subcontratado */}
                    <M_DrawerTableTextField
                        width="100%"
                        tableLabel={t('Carrier')}
                        label={t('Carrier')}
                        entity="Carrier"
                        value={
                            dataMovements?.CarrierId
                                ? { CarrierId: dataMovements.CarrierId, Name: dataMovements.Name }
                                : null
                        }
                        filterField="Name"
                        labelFields={['Name']}
                        onSubmitValue={obj =>
                            setDataMovements(prev => ({
                                ...prev,
                                Name: obj ? obj.Name : null,
                                CarrierId: obj ? obj.CarrierId : null,
                            }))
                        }
                        valueField="CarrierId"
                    />

                    <M_InputTextField
                        type="decimal"
                        label={t("AgreedValue")}
                        value={dataMovements.AgreedValue || ''}
                        onChange={value => {
                            setDataMovements(prev => ({
                                ...prev,
                                AgreedValue: value.target.value
                            }))
                        }}
                        iconEnd={<EuroSymbol />}
                    />

                    <M_TextArea
                        label={t('Notes')}
                        value={dataMovements.Notes || ''}
                        onChange={value => {
                            setDataMovements(prev => ({
                                ...prev,
                                Notes: value.target.value
                            }))
                        }}
                    />

                    <M_DrawerTableTextField
                        width="100%"
                        tableLabel={t('Costs')}
                        label={t('Costs')}
                        entity="Costs"
                        multiple
                        value={selectedSubcontractedCosts}
                        filterField="Descriptive"
                        labelFields={['Descriptive']}
                        onSubmitValue={setSubcontractedCosts}
                        valueField="CostsId"
                        FILTER={`IsSubcontracted eq true`}
                        placeholder={t("SelectCosts")}
                    />

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <strong>{t('Costs')}</strong>
                        {selectedSubcontractedCosts.length > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                {selectedSubcontractedCosts.map((cost) => (
                                    <div
                                        key={cost.CostsId}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '10px',
                                            padding: '6px 8px',
                                            border: '1px solid var(--color-grey-light)',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        <span>
                                            {cost.Descriptive || t('NoData')}
                                            {cost.AddicionalPrice !== null && cost.AddicionalPrice !== undefined
                                                ? ` - ${cost.AddicionalPrice} EUR`
                                                : ''}
                                        </span>
                                        <DeleteOutline
                                            onClick={() => removeSubcontractedCost(cost.CostsId)}
                                            style={{ cursor: 'pointer', color: 'var(--color-red)' }}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div style={{fontSize: '12px'}}>{t('NoCosts')}</div>
                        )}
                    </div>
                </div>
            )}

            {dataChangeState === 'RS' && (
                <M_TextArea
                    label={t('CancellationReason')}
                    value={dataMovements.Notes || ''}
                    onChange={value => {
                        setDataMovements(prev => ({
                            ...prev,
                            Notes: value.target.value
                        }))
                    }}
                />
            )}
        </div>
    )
}

export default function OrderBoard({ roles }) {

    const [line, setLine] = useState(null)
    const [modalChangeState, setModalChangeState] = useState(false)
    const [dataMovements, setDataMovements] = useState({})
    const [dataChangeState, setDataChangeState] = useState('')
    const [dataChangeStateId, setDataChangeStateId] = useState(null)
    const [optionsState, setOptionsState] = useState([])
    const [historyMovements, setHistoryMovements] = useState([])
    const [modalHistoryMovements, setModalHistoryMovements] = useState(false)
    const [refresh, setRefresh] = useState(false)
    const [atributeTrinomial, setAtributeTrinomial] = useState(false)
    const [trinomials, setTrinomials] = useState([])
    const [infoTrinomial, setInfoTrinomial] = useState(false)
    const bufferServiceHistoryMovements = new BufferService('ServiceOrderChangeState')
    const getMovementType = new BufferService('ServiceOrderStateMovementType')
    const submitMovement = new BufferService('ServiceOrderChangeState/change-state')
    const getAvailableTrinomials = new BufferService('ServiceOrderTrinomialCompatibilityView')
    const ctx = useContext(LoadingContext)
    const ctx2 = useContext(NavigateContext)
    const { t } = useTranslation()

    const optionsTrinomial = [
        { Name: 'Trinomial', NormalizedName: t('Trinomial'), type: 'string', IsDisabled: false, IsRequired: true, IsHiddenList: false, ForeignTableToPassId: 'TrinomialAssignId', ForeignTableName: 'ServiceOrderTrinomialCompatibilityView', ForeignKeyName: 'TransportLicensePlate', filter: 'serviceOrderId eq 7' },
    ]

    const changeState = () => {
        if (line) {
            ctx.setIsLoad(true)
            const filter = `?$filter=ServiceOrderStateId eq ${line[0].ServiceOrderStateId} &$orderby=ServiceOrderMovementTypeId asc`

            getMovementType.filter(filter).then(response => {
                if (response.name === 'AxiosError') {
                    ctx.toastMessages('error', response.response.data.Errors.toString(), '')
                    ctx.setIsLoad(false)
                    return
                }
                setOptionsState(response.Items)
                setModalChangeState(true)
                ctx.setIsLoad(false)
            })

        } else {
            ctx.toastMessages('warning', t("SelectedLine"), '')
        }
    }

    const submitChangeState = () => {
        const tempObject = {
            ServiceOrderId: line[0].ServiceOrderId,
            ServiceOrderMovementTypeId: dataChangeStateId,
            MovDate: new Date().toISOString(),
            ...dataMovements
        }
        if (!tempObject.ServiceOrderMovementTypeId) {
            ctx.toastMessages("error", '', t("SelectOneMovement"))
            return
        }

        submitMovement.post(tempObject).then(response => {
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', response.response.data.Errors.toString(), '')
                return
            }
            ctx.toastMessages('success', t("ChangeStateSuccess"), '')
            setDataMovements({})
            setDataChangeState('')
            setDataChangeStateId(null)
            setModalChangeState(false)
            setRefresh(true)
        })
    }

    const viewOrderService = () => {
        if (line) {
            const current = ctx.passValue instanceof Map ? ctx.passValue : new Map()
            const newMap = new Map(current)
            newMap.set("lines", line[0])

            ctx.setPassValue(newMap)

            ctx2.newTab('viewOrderService', `${t("NumberOrder")} #${line[0].ServiceOrderId}`)
        } else {
            ctx.toastMessages('warning', t("SelectedLine"), '')
        }
    }

    const viewHistoryMovements = () => {
        if (line) {
            ctx.setIsLoad(true)
            bufferServiceHistoryMovements.filter(`?filter=serviceOrderId eq ${line[0].ServiceOrderId}`).then((respHistory) => {
                if (respHistory.name === 'AxiosError') {
                    ctx.toastMessages('error', respHistory.response.data.Errors[0], '')
                    return
                }
                setHistoryMovements(respHistory.Items)
                setModalHistoryMovements(true)
                ctx.setIsLoad(false)
            })
        } else {
            ctx.toastMessages('warning', t("SelectedLine"), '')
        }
    }

    const operationOrderService = useCallback((operation) => {
        if (!line || !line[0]) {
            ctx.toastMessages('warning', t("SelectedLine"), '')
            return
        }

        const current = ctx.passValue instanceof Map ? ctx.passValue : new Map()
        const newMap = new Map(current)

        newMap.set('operation', operation)
        newMap.set('lines', line[0])

        ctx.setPassValue(newMap)

        if (operation === 'edit') {
            ctx2.newTab('createOrderService', t("EditOrderService"))
        }
    }, [ctx, ctx2, t, line])

    const getTrinomials = (page = 1, pageSize = 20) => {
        if (!line || !line[0]) {
            ctx.toastMessages('warning', t("SelectedLine"), '')
            return
        }

        const serviceOrderId = line[0].ServiceOrderId

        getAvailableTrinomials.filter(
            `/query?page=${page}&pagesize=${pageSize}&serviceOrderId=${serviceOrderId}`
        ).then((resp) => {
            if (resp.name === 'AxiosError') {
                ctx.toastMessages('error', resp.response.data.Errors[0], '')
                return
            }
            setTrinomials(prev => {
                if (page === 1) return resp.Items

                const existingIds = new Set(prev.map(item => item.TrinomialAssignId))
                const newItems = resp.Items.filter(item => !existingIds.has(item.TrinomialAssignId))
                return [...prev, ...newItems]
            })
        })
    }

    const openTrinomialDrawer = () => {
        setAtributeTrinomial(true)
        getTrinomials(1)
    }


    return (
        <div>
            <M_Table
                entityName={'ServiceOrderDemandBoardView'}
                rolePermissions={roles}
                filtersOnTable
                refresh={refresh}
                setRefresh={setRefresh}
                selectSingle
                rowClassName={getOrderBoardRowClassName}
                onPassLine={(line) => {
                    if (!line || (Array.isArray(line) && line.length === 0)) {
                        setLine(null)
                    } else {
                        setLine(line)
                    }
                }}
                extraButtons={[
                    { label: t('ChangeMovement'), element: 'default', source: '/icons/svgsDaNet/arrows-shuffle.svg', style: {}, command: () => { changeState() } },
                    { label: t("EditOrderService"), element: 'default', source: '/icons/svgsDaNet/edit.svg', style: {}, command: () => { operationOrderService('edit') } },
                    { label: t('OrderServiceView'), element: 'default', source: '/icons/svgsDaNet/eye-blue.svg', style: {}, command: () => { viewOrderService() } },
                    { label: t('HistoryMovements'), element: 'default', source: '/icons/svgsDaNet/history.svg', style: {}, command: () => { viewHistoryMovements() } }
                ]}
            />

            <M_MessageBoard
                open={modalChangeState}
                title={t('ChangeMovement')}
                onClose={() => setModalChangeState(false)}
                body={<div style={{ minWidth: '300px' }}>
                    <M_Select
                        label={t('SelectMovement')}
                        options={optionsState.map(state => ({
                            label: state.MovementType,
                            value: state.MovementTypeCode
                        }))}
                        value={dataChangeState || ''}
                        onChange={value => {
                            const selectedMovement = optionsState.find(state => state.MovementTypeCode === value.target.value)
                            setDataChangeState(value.target.value)
                            setDataChangeStateId(selectedMovement?.ServiceOrderMovementTypeId ?? null)
                            setDataMovements({})
                        }}
                    />
                    <div>
                        <Body
                            dataChangeState={dataChangeState}
                            dataMovements={dataMovements}
                            setDataMovements={setDataMovements}
                            openTrinomialDrawer={openTrinomialDrawer}
                            optionsTrinomial={optionsTrinomial}
                            serviceOrderId={line?.[0]?.ServiceOrderId}
                            setInfoTrinomial={setInfoTrinomial}
                            t={t}
                        />

                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '10px', gap: '5px' }}>
                        <img src="/icons/svgsDaNet/alert-triangle.svg" alt="alert" style={{ width: '20px' }} />
                        {t("InfoSendEmailChangeState")}
                    </div>
                </div>}
                footer={<div className='divFooterAlertsOrderService'>
                    <M_Button
                        backgroundColor={'var(--color-red)'}
                        label={t("Close")}
                        labelColor={'var(--color-white)'}
                        onClick={() => {
                            setModalChangeState(false)
                            setDataChangeState('')
                            setDataChangeStateId(null)
                            setDataMovements({})
                        }}
                    />
                    <M_Button
                        backgroundColor={'var(--color-green)'}
                        label={t("Save")}
                        labelColor={'var(--color-white)'}
                        onClick={submitChangeState}
                    />
                </div>}
            />

            <M_MessageBoard
                open={modalHistoryMovements}
                title={t('HistoryMovements')}
                onClose={() => setModalHistoryMovements(false)}
                minWidth={'80%'}
                body={
                    historyMovements.length !== 0 ? (
                        <div style={{ width: '100%' }}>
                            <div className='tableHistoryMovementsOrderService'>
                                <div style={{ textAlign: 'center' }}>{t("Status")}</div>
                                <div style={{ textAlign: 'center' }}>{t("DateChange")}</div>
                                <div style={{ textAlign: 'center' }}>{t("Trinomial")}</div>
                                <div style={{ textAlign: 'center' }}>{t("Notes")} </div>
                                <div style={{ textAlign: 'center' }}>{t("MovementName")}</div>
                                <div style={{ textAlign: 'center' }}>{t("CreatedBy")} </div>
                            </div>
                            <div>
                                {historyMovements?.map((elem, key) => (
                                    <div key={key} className='divRowHistoryMovementsOrderService'>
                                        <div style={{ textAlign: 'center', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                                            <div style={{ backgroundColor: elem.ServiceOrderWorkflowFromStateColor || "#000000" }} className='divIndicatorStatusHistoryMovementsOrderService'>
                                                {elem.ServiceOrderWorkflowFromState}
                                            </div>
                                            <span>→</span>
                                            <div style={{ backgroundColor: elem.ServiceOrderWorkflowToStateColor || "#000000" }} className='divIndicatorStatusHistoryMovementsOrderService'>
                                                {elem.ServiceOrderWorkflowToState}
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'center' }}>
                                            {new Date(elem.MovDate).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                        </div>
                                        <div style={{ textAlign: 'center' }}>{elem.TransportName ? `${elem.TrailerName} + ${elem.DriverName} + ${elem.TransportName}` : t("NoData")}</div>
                                        <div style={{ textAlign: 'center' }}>{elem.Notes || t("NoData")}</div>
                                        <div style={{ textAlign: 'center' }}>{elem.ServiceOrderMovementTypeName || t("NoData")}</div>
                                        <div style={{ textAlign: 'center' }}>{elem.CreatedBy || t("NoData")}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : t("NoData")
                }
                footer={
                    <M_Button
                        backgroundColor={'var(--color-red)'}
                        label={t("Close")}
                        labelColor={'var(--color-white)'}
                        onClick={() => {
                            setModalHistoryMovements(false)
                            setHistoryMovements([])
                        }}
                    />
                }
            />

            <M_ElementDrawer
                title={t("EditOrderService")}
                width={'50%'}
                open={atributeTrinomial}
                setOpen={setAtributeTrinomial}
                onClose={() => setAtributeTrinomial(false)}
                element={
                    <div className='trinomialDrawerContent'>

                        <div className='trinomialDrawerList'>
                            {trinomials.length > 0 ? trinomials.map((elem, key) => (
                                <TrinomialCard
                                    key={elem.TrinomialAssignId || key}
                                    trinomial={elem}
                                    selected={dataMovements.TrinomialAssignId === elem.TrinomialAssignId}
                                    t={t}
                                    onSelect={() => {
                                        setDataMovements(prev => ({
                                            ...prev,
                                            TrinomialAssignId: elem.TrinomialAssignId,
                                            Transport: elem.TransportLicensePlate,
                                            TransportId: elem.TransportId,
                                            Trailer: elem.TrailerLicensePlate,
                                            TrailerId: elem.TrailerId,
                                            Driver: elem.DriverName,
                                            DriverId: elem.DriverId,
                                        }))
                                        setAtributeTrinomial(false)
                                    }}
                                />
                            )) : (
                                <div className='trinomialDrawerEmptyState'>{t('NoData')}</div>
                            )}
                        </div>
                    </div>
                }
            />

            <M_MessageBoard
                open={infoTrinomial}
                title={t("TrinomialInfo")}
                warning
                onClose={() => setInfoTrinomial(false)}
                body={
                    <div style={{ minWidth: '300px' }}>
                        <b>{t("BodyTrinomialInfo1")}</b>
                        <p>{t("BodyTrinomialInfo2")}</p>
                        <p>{t("BodyTrinomialInfo3")}</p>
                        <p>{t("BodyTrinomialInfo4")}</p>
                        <p>{t("BodyTrinomialInfo5")}</p>
                        <p>{t("BodyTrinomialInfo6")}</p>
                    </div>
                }
                footer={
                    <M_Button
                        backgroundColor={'var(--color-red)'}
                        label={t("Close")}
                        labelColor={'var(--color-white)'}
                        onClick={() => setInfoTrinomial(false)}
                    />
                }
            />
        </div>
    )
}
